import React, { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import classNames from "classnames/bind";
// html2canvas is loaded dynamically on demand (202KB — not needed on initial render)
import CustomSelect from "../custom-select/CustomSelect";
import styles from "./ReportProblemButton.module.scss";
import { useUploadReportScreenshotMutation } from "../../../api/uploadsApi";
import { useSubmitBugReportMutation } from "../../../api/bugReportsApi";
import { useAppSelector } from "../../../store";
import { getConsoleLogs } from "../../../utils/console-capture";
import { tokens } from "../../../styles/tokens";

const cx = classNames.bind(styles);

const FlagIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ScreenshotIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const PROBLEM_TYPES = [
  "translation_error",
  "content_error",
  "technical_bug",
  "other",
] as const;

type ProblemType = (typeof PROBLEM_TYPES)[number];

interface ReportProblemButtonProps {
  pageInfo?: string;
}

interface GalleryImage {
  blob: Blob;
  previewUrl: string;
}

const ReportProblemButton: React.FC<ReportProblemButtonProps> = ({ pageInfo: pageInfoProp }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [problemType, setProblemType] = useState<ProblemType>("content_error");
  const [description, setDescription] = useState("");
  const [screenshotBlob, setScreenshotBlob] = useState<Blob | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureFailed, setCaptureFailed] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const currentUser = useAppSelector((state) => state.session.user);
  const [uploadScreenshot] = useUploadReportScreenshotMutation();
  const [submitBugReport] = useSubmitBugReportMutation();

  // Auto-detect page info from location if not provided via prop
  const pageInfo = pageInfoProp || `${document.title} (${location.pathname})`;

  const captureScreenshot = useCallback(async () => {
    setIsCapturing(true);
    setCaptureFailed(false);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const capturePromise = html2canvas(document.body, {
        useCORS: true,
        allowTaint: false,
        logging: false,
        scale: 1,
        backgroundColor: "#ffffff",
        // Capture only the visible viewport — full body can exceed
        // Safari/iOS canvas size limits (~16MP) and hang silently
        width: window.innerWidth,
        height: window.innerHeight,
        x: window.scrollX,
        y: window.scrollY,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        foreignObjectRendering: false,
        ignoreElements: (element) => {
          if (element.id === "report-problem-modal" || element.id === "report-problem-fab") return true;
          return false;
        },
      });
      // Timeout after 8s — html2canvas can hang indefinitely on Safari
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("Screenshot capture timed out")), 8000)
      );
      const canvas = await Promise.race([capturePromise, timeoutPromise]);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png", 0.8)
      );
      if (blob) {
        setScreenshotBlob(blob);
        setScreenshotPreview(URL.createObjectURL(blob));
      } else {
        setCaptureFailed(true);
      }
    } catch {
      // Auto-capture failed or timed out — show manual upload fallback
      setCaptureFailed(true);
    } finally {
      setIsCapturing(false);
    }
  }, []);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScreenshotBlob(file);
    setScreenshotPreview(URL.createObjectURL(file));
    setCaptureFailed(false);
  }, []);

  const handleGalleryUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const maxTotal = 5;
    const remaining = maxTotal - galleryImages.length;
    const toAdd = Array.from(files).slice(0, remaining);
    const newImages: GalleryImage[] = toAdd.map((file) => ({
      blob: file,
      previewUrl: URL.createObjectURL(file),
    }));
    setGalleryImages((prev) => [...prev, ...newImages]);
    // Reset input so same file can be re-selected
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  }, [galleryImages.length]);

  const removeGalleryImage = useCallback((index: number) => {
    setGalleryImages((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].previewUrl);
      next.splice(index, 1);
      return next;
    });
  }, []);

  const handleOpen = () => {
    setSent(false);
    setDescription("");
    setProblemType("content_error");
    setCaptureFailed(false);
    setGalleryImages([]);
    setIsOpen(true);

    // Always try auto-capture first, fall back to manual upload on failure
    setIncludeScreenshot(true);
    captureScreenshot();
  };

  const handleClose = () => {
    setIsOpen(false);
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview);
    }
    setScreenshotBlob(null);
    setScreenshotPreview(null);
    setCaptureFailed(false);
    galleryImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    setGalleryImages([]);
  };

  const handleSubmit = async () => {
    if (!description.trim() || !currentUser?.uid) return;

    setIsSending(true);
    try {
      let screenshotUrl: string | null = null;

      // Upload auto-captured screenshot if enabled
      if (includeScreenshot && screenshotBlob) {
        const formData = new FormData();
        formData.append("file", screenshotBlob, "report-screenshot.png");

        const uploadResult = await uploadScreenshot(formData).unwrap();
        screenshotUrl = uploadResult.url;
      }

      // Upload gallery images in parallel
      const extraScreenshotUrls: string[] = [];
      if (galleryImages.length > 0) {
        const uploadPromises = galleryImages.map(async (img, i) => {
          const fd = new FormData();
          const ext = img.blob.type?.split("/")[1] || "png";
          fd.append("file", img.blob, `gallery-screenshot-${i + 1}.${ext}`);
          const result = await uploadScreenshot(fd).unwrap();
          return result.url;
        });
        const urls = await Promise.all(uploadPromises);
        extraScreenshotUrls.push(...urls);
      }

      // Submit via the bug reports API
      await submitBugReport({
        problem_type: problemType,
        description: description.trim(),
        page_url: window.location.href,
        page_info: pageInfo,
        screenshot_url: screenshotUrl,
        extra_screenshot_urls: extraScreenshotUrls,
        console_logs: getConsoleLogs(),
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        language: i18n.language,
      }).unwrap();

      setSent(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch {
      // Error is handled by RTK Query baseQuery (shows error popup)
    } finally {
      setIsSending(false);
    }
  };

  if (!currentUser) return null;

  return (
    <>
      <button
        id="report-problem-fab"
        className={cx("fab")}
        data-tour="report-bug-fab"
        onClick={handleOpen}
        title={t("report.title")}
        aria-label={t("report.title")}
      >
        <FlagIcon />
      </button>

      {isOpen && (
        <div id="report-problem-modal" style={{ position: "fixed", inset: 0, zIndex: 9999 }}>
          <div className={cx("overlay")} onClick={handleClose} />
          <div className={cx("modal")} ref={modalRef} role="dialog" aria-modal="true">
            <div className={cx("header")}>
              <h3 className={cx("headerTitle")}>{t("report.title")}</h3>
              <button className={cx("closeButton")} onClick={handleClose} aria-label={t("report.close")}>
                <CloseIcon />
              </button>
            </div>

            {sent ? (
              <div className={cx("successState")}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={tokens.indigo600} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <p>{t("report.sent")}</p>
              </div>
            ) : (
              <div className={cx("body")}>
                <div className={cx("field")}>
                  <label className={cx("label")}>{t("report.type")}</label>
                  <CustomSelect
                    options={PROBLEM_TYPES.map((type) => ({ value: type, label: t(`report.types.${type}`) }))}
                    value={problemType}
                    onChange={(val) => setProblemType(val as ProblemType)}
                    className={cx("select")}
                  />
                </div>

                <div className={cx("field")}>
                  <label className={cx("label")}>{t("report.description")}</label>
                  <textarea
                    className={cx("textarea")}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("report.descriptionPlaceholder")}
                    rows={4}
                    maxLength={1000}
                  />
                  <span className={cx("charCount")}>{description.length}/1000</span>
                </div>

                <div className={cx("screenshotSection")}>
                  <label className={cx("screenshotToggle")}>
                    <input
                      type="checkbox"
                      checked={includeScreenshot}
                      onChange={(e) => setIncludeScreenshot(e.target.checked)}
                    />
                    <ScreenshotIcon />
                    <span>{t("report.includeScreenshot")}</span>
                  </label>
                  {includeScreenshot && (
                    isCapturing ? (
                      <div className={cx("screenshotLoading")}>
                        {t("report.capturingScreenshot", "Capturing screenshot...")}
                      </div>
                    ) : screenshotPreview ? (
                      <div className={cx("screenshotPreview")}>
                        <img src={screenshotPreview} alt={t("report.screenshot")} />
                      </div>
                    ) : (captureFailed || isSafari) ? (
                      <div className={cx("screenshotUpload")}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          className={cx("fileInput")}
                        />
                        <button
                          type="button"
                          className={cx("uploadButton")}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <ScreenshotIcon />
                          {t("report.uploadScreenshot", "Upload screenshot")}
                        </button>
                      </div>
                    ) : null
                  )}
                </div>

                {/* Gallery upload — upload screenshots from device */}
                <div className={cx("gallerySection")}>
                  <label className={cx("galleryLabel")}>
                    <ScreenshotIcon />
                    <span>{t("report.uploadFromGallery", "Upload from gallery")}</span>
                    <span className={cx("galleryCount")}>{galleryImages.length}/5</span>
                  </label>

                  {galleryImages.length > 0 && (
                    <div className={cx("galleryGrid")}>
                      {galleryImages.map((img, i) => (
                        <div key={i} className={cx("galleryItem")}>
                          <img src={img.previewUrl} alt={`${t("report.screenshot")} ${i + 1}`} />
                          <button
                            type="button"
                            className={cx("galleryRemove")}
                            onClick={() => removeGalleryImage(i)}
                            aria-label={t("report.removeScreenshot", "Remove")}
                          >
                            <CloseIcon />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {galleryImages.length < 5 && (
                    <div className={cx("screenshotUpload")}>
                      <input
                        ref={galleryInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        multiple
                        onChange={handleGalleryUpload}
                        className={cx("fileInput")}
                      />
                      <button
                        type="button"
                        className={cx("uploadButton")}
                        onClick={() => galleryInputRef.current?.click()}
                      >
                        <ScreenshotIcon />
                        {t("report.addScreenshot", "Add screenshot")}
                      </button>
                    </div>
                  )}
                </div>

                <div className={cx("pageContext")}>
                  <span className={cx("contextLabel")}>{t("report.page")}:</span>
                  <span className={cx("contextValue")}>{pageInfo}</span>
                </div>

                <button
                  className={cx("submitButton")}
                  onClick={handleSubmit}
                  disabled={!description.trim() || isSending || isCapturing}
                >
                  {isSending ? t("report.sending") : t("report.submit")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReportProblemButton;
